import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { TextZoom } from '@capacitor/text-zoom';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { CssBaseline, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { captureException } from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { HttpStatusCode, PejHttpError } from '@common-lib';
import {
  createPortalTheme,
  mapThemeToPortalThemeOptions,
} from '@frontend-libs/portal';

import {
  configurationClient,
  PortalPublicConfigurationModel,
} from '@portal-clients/configuration';

import {
  KeyValueStore,
  MetadataContextState,
  MetadataProvider,
} from '@frontend/utils';
import { Loading } from '@frontend/views';

import { portalConfigurationCache } from './cache';
import { PortalPublicConfigurationContext } from './portal-public-configuration-context';
import { PortalConfigurationError } from './portal-public-configuration-error.component';

declare module '@mui/material/styles' {
  interface AppSpecificStyles {}
}

type ErrorTypes = 'NOT_FOUND' | 'LOCKED' | 'FAILED_TO_INITIALIZE' | 'UNKNOWN';

export const PortalPublicConfigurationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [configuration, setConfiguration] = useState<
    PortalPublicConfigurationModel | undefined
  >(undefined);

  const [loadError, setLoadError] = useState<ErrorTypes | undefined>();
  const [metadata, setMetadata] = useState<MetadataContextState | undefined>();

  const theme = useMemo(
    () =>
      createPortalTheme(
        configuration?.theme
          ? mapThemeToPortalThemeOptions(configuration?.theme)
          : {},
      ),
    [configuration],
  );

  useEffectOnce(() => {
    portalConfigurationCache
      .get()
      .then((cached) => {
        if (cached && cached.appVersionHash === __APP_VERSION_HASH__) {
          setConfiguration(cached);
        }
      })
      .then(() => {
        configurationClient
          .getPublic()
          .then((response) => {
            return portalConfigurationCache
              .set({
                ...response,
                appVersionHash: __APP_VERSION_HASH__,
              })
              .then(() => setConfiguration(response));
          })
          .catch((error) => {
            if (error instanceof PejHttpError) {
              if (error.statusCode === HttpStatusCode.NOT_FOUND) {
                setLoadError('NOT_FOUND');
                return;
              } else if (error.statusCode === HttpStatusCode.LOCKED) {
                setLoadError('LOCKED');
                return;
              }
            }

            setLoadError('UNKNOWN');
          });
      })
      .catch((error) => {
        console.error(error);
        captureException(error);
      });
  });

  const posthog = usePostHog();

  useEffect(() => {
    if (!configuration) {
      return;
    }

    document.documentElement.lang = configuration.defaultLocale;

    Promise.all([
      Device.getInfo(),
      Device.getLanguageTag(),
      CapacitorUpdater.getDeviceId(),
      Capacitor.isNativePlatform() ? App.getInfo() : Promise.resolve(undefined),
      KeyValueStore.get('anon_device_id'),
      Capacitor.isPluginAvailable('text-zoom')
        ? TextZoom.getPreferred().then((result) => result.value)
        : Promise.resolve(undefined),
    ])
      .then(
        ([
          deviceInfo,
          languageTag,
          { deviceId },
          appInfo,
          anonDeviceId,
          preferredTextZoom,
        ]) => {
          // It is a bonus to re-use the capgo deviceId but it is not available
          // for web so we generate one ourselves. They have a very similar
          // implementation, the difference is that they store it using native
          // storage methods and we use localstorage.
          if (!deviceId) {
            if (!anonDeviceId) {
              anonDeviceId = crypto.randomUUID();
              KeyValueStore.set('anon_device_id', anonDeviceId);
            }
          }

          const version = Capacitor.isNativePlatform()
            ? appInfo!.version
            : APP_CONFIG.version;

          const newMetadata: MetadataContextState = {
            deviceId,
            organizationId: configuration.organizationId,
            operatorId: configuration.operatorId,
            portalId: configuration.portalId,
            portalDisplayName: configuration.displayName,
            appId: appInfo?.id ?? 'web',
            iosAppStoreId: configuration.iosAppStoreId,
            appName: appInfo?.name ?? 'web',
            appBuild: appInfo?.build ?? 'web',
            appVersion: version,
            bundleVersion: APP_CONFIG.version,
            url: APP_CONFIG.baseUrl
              ? APP_CONFIG.baseUrl
              : window.location.origin,
            environment: APP_CONFIG.environment,
            stack: APP_CONFIG.stack,
            osVersion: deviceInfo.osVersion,
            androidSDK: deviceInfo.androidSDKVersion,
            manufacturer: deviceInfo.manufacturer,
            model: deviceInfo.model,
            platform: deviceInfo.platform,
            languageTag: languageTag.value,
            preferredTextZoom: preferredTextZoom,
          };

          setMetadata(newMetadata);

          posthog.identify(deviceId || anonDeviceId!, {
            // Only send properties that are not user specific. This allows
            // anonymous analytics for apps.
            anonymous_device_id: newMetadata.deviceId,
            organization_id: newMetadata.organizationId,
            operator_id: newMetadata.operatorId,
            app_portal_id: newMetadata.portalId,
            app_id: newMetadata.appId,
            app_name: newMetadata.appName,
            app_build: newMetadata.appBuild,
            app_version: newMetadata.appVersion,
            bundle_version: newMetadata.bundleVersion,
            url: newMetadata.url,
            environment: newMetadata.environment,
            stack: newMetadata.stack,
            $os_version: newMetadata.osVersion,
            android_sdk: newMetadata.androidSDK,
            manufacturer: newMetadata.manufacturer,
            model: newMetadata.model,
            platform: newMetadata.platform,
            language_tag: newMetadata.languageTag,
            preferred_text_zoom: newMetadata.preferredTextZoom,
          });

          Sentry.setTags({
            app_id: newMetadata.appId,
            app_name: newMetadata.appName,
            app_build: newMetadata.appBuild,
            app_version: newMetadata.appVersion,
            bundle_version: newMetadata.bundleVersion,
            organization_id: newMetadata.organizationId,
            operator_id: newMetadata.operatorId,
            app_portal_id: newMetadata.portalId,
          });
        },
      )
      .catch((error) => {
        console.error(error);
        Sentry.captureException(
          new Error('Failed to initialize app', {
            cause: error,
          }),
          {
            level: 'fatal',
          },
        );
        setLoadError('FAILED_TO_INITIALIZE');
      });
  }, [configuration]);

  return (
    <CssBaseline>
      <meta name="theme-color" content={theme.page.color} />

      <link
        rel="icon"
        type="image/x-icon"
        href={configuration?.images?.favicon ?? '/assets/favicon.ico'}
      />

      {loadError === 'NOT_FOUND' && (
        <PortalConfigurationError
          title="Incorrect Link"
          errorMessage="Contact your Site for the correct link."
          showRetry={false}
        />
      )}
      {loadError === 'LOCKED' && (
        <PortalConfigurationError errorMessage="App has been disabled. Please try again later." />
      )}
      {loadError === 'FAILED_TO_INITIALIZE' && (
        <PortalConfigurationError errorMessage="Failed to initialize app" />
      )}

      {(loadError === 'UNKNOWN' || loadError === undefined) && (
        <>
          {configuration && metadata ? (
            <PortalPublicConfigurationContext.Provider value={configuration}>
              <MetadataProvider value={metadata}>
                <ThemeProvider theme={theme}>{children}</ThemeProvider>
              </MetadataProvider>
            </PortalPublicConfigurationContext.Provider>
          ) : loadError ? (
            <PortalConfigurationError errorMessage="Failed to load app configuration" />
          ) : (
            <Loading />
          )}
        </>
      )}
    </CssBaseline>
  );
};
